import * as THREE from 'three';
import type { CivilianDefinition, MapDefinition } from '../../../shared/game/world-types.js';
import { terrainHeight } from '../../../shared/game/terrain.js';
import { caveAt, levelMap } from '../../../shared/game/caves.js';
import { civilianPlace } from '../../../shared/game/civilian-walk.js';
import { inPartyVision, type FogState } from '../../../shared/game/fog-types.js';
import { VisibleUnitGroup } from '../scene-transforms.js';
import { createResidentFigure } from './resident-figure.js';
import type { CharacterAnimator } from './character-animator.js';
import { CARRIED_HANDLE_RADIUS, carriedProp } from './activity-props.js';
import { poseWeaponGrip, weaponGripMount } from './weapon-grip.js';
import type { UnitState } from '../../../shared/game/world-types.js';

/** Poses a second at most for a resident: every frame at 60 fps, every other one at 144. */
const RESIDENT_RATE = 70;

/**
 * Neutral scenery actors have no unit plates, picking proxies, selection rings or combat state. Their figures are built one at a time
 * (`buildNext`, a few hundred milliseconds each), so the scene can spread a map's residents over loading frames.
 */
export class CivilianActors {
  private residents: { definition: CivilianDefinition; group: THREE.Group; animator: CharacterAnimator; state: UnitState; posedAt: number; carried?: THREE.Group }[] = [];

  constructor(private scene: THREE.Scene, private definitions: CivilianDefinition[], private map?: MapDefinition) {}

  /** Residents whose figures are still to be built, and the one `buildNext` builds. */
  get remaining() { return this.definitions.length - this.residents.length; }
  get next(): CivilianDefinition | undefined { return this.definitions[this.residents.length]; }
  buildNext() {
    const definition = this.next; if (!definition) return;
    const group = new VisibleUnitGroup(); group.name = `civilian:${definition.id}`;
    const { rig, animator, state } = createResidentFigure(definition);
    // A walker turns its own figure as it goes (the animator's facing); one that stays put is turned by its group.
    this.place(group, animator, definition);
    if (!definition.walk) group.rotation.y = definition.rotation;
    group.add(rig.root); group.visible = false; this.scene.add(group);
    const carried = definition.walk?.carry ? carriedProp(definition.walk.carry) : undefined;
    if (carried) group.add(carried);
    this.residents.push({ definition, group, animator, state, posedAt: -Infinity, carried });
  }

  /** Stands the figure on the ground under `point`: a cave's floor under the Hollow's roofs, else the land. */
  private place(group: THREE.Group, animator: CharacterAnimator, point: { x: number; z: number }) {
    const map = this.map, ground = map && levelMap(map, caveAt(map, point.x, point.z));
    group.position.set(point.x, ground ? terrainHeight(ground, point.x, point.z) : 0, point.z);
    if (ground?.terrain) animator.groundHeight = (x, z) => terrainHeight(ground, x, z);
  }

  update(seconds: number, fog?: FogState, lobby = false, inView?: (position: THREE.Vector3, height: number) => boolean) {
    for (const resident of this.residents) {
      const { definition, group, animator, state } = resident, shown = group.visible;
      const place: ReturnType<typeof civilianPlace> = definition.walk ? civilianPlace(definition, seconds) : definition;
      if (definition.walk) this.place(group, animator, place);
      group.visible = (fog ? !!fog.revealed || inPartyVision(fog, place) : lobby) && (!inView || inView(group.position, animator.rig.height));
      if (!group.visible) continue;
      if (definition.walk) {
        // A walker poses every frame (its feet must keep pace with the ground), takes up each stop's activity and faces the stop's way there.
        const stop = place.stop, activity = stop?.activity ?? definition.activity;
        if (stop && animator.activity() !== activity) animator.setActivity(activity, definition.phaseSeconds);
        state.standFacing = stop?.rotation;
        resident.posedAt = seconds; animator.update(state, place, seconds);
        if (resident.carried) this.carry(resident.carried, group, animator, !stop);
        continue;
      }
      // Residents only idle in place, so above RESIDENT_RATE their poses refresh on alternate frames. One coming into view,
      // or a step back in time, re-poses at once.
      if (!shown || seconds - resident.posedAt >= 1 / RESIDENT_RATE || seconds < resident.posedAt) { resident.posedAt = seconds; animator.update(state, definition, seconds); }
    }
  }

  /**
   * A walker's load: in the left fist, the handle across the fingers like a weapon's grip, while it walks; set down where the hand let go of it
   * when it stops, until it walks on.
   */
  private carry(carried: THREE.Group, group: THREE.Group, animator: CharacterAnimator, walking: boolean) {
    const arm = animator.rig.leftArm, scale = animator.rig.proportions.handScale;
    if (walking) {
      if (carried.parent !== arm.hand) { arm.hand.add(carried); carried.position.copy(weaponGripMount(-1, scale, CARRIED_HANDLE_RADIUS)); carried.rotation.set(0, 0, 0); group.updateMatrixWorld(true); carried.scale.setScalar(1 / (arm.hand.getWorldScale(new THREE.Vector3()).x || 1)); }
      poseWeaponGrip(arm, 1, scale, CARRIED_HANDLE_RADIUS);
    } else if (carried.parent !== group) {
      group.updateMatrixWorld(true);
      const at = carried.getWorldPosition(new THREE.Vector3());
      group.add(carried); carried.scale.setScalar(1); carried.rotation.set(0, animator.rig.root.rotation.y, 0);
      // On the ground under where it hung: the load's bottom sits on the ground (the group stands on it).
      carried.position.set(at.x - group.position.x, .38, at.z - group.position.z);
    }
  }

  /** Geometry/materials are owned and disposed by the surrounding scene. */
  dispose() { for (const resident of this.residents) resident.animator.dispose(); }
}
